import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { CONTACT_QUERY_KEY } from './useContact';

export function useContactKeyFactsUpdateMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: Api.IContact) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			contactId,
			keyFactsCollection,
			contactData,
		}: {
			contactId: string;
			keyFactsCollection: Api.IKeyFact[];
			contactData: Api.IContact;
		}) => {
			// Merge the key facts update with the provided contact data
			const entityToSave = Api.excludeKeysOf({ ...contactData, keyFactsCollection }, ['keyFacts']) as Api.IContact;

			return userSession.webServiceHelper.callAsync<Api.IContact>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `contact/${encodeURIComponent(contactId)}`,
					queryParams: {
						expand: 'HouseholdMembers',
						allowDuplicateEmail: true,
					},
				}),
				'PUT',
				entityToSave
			);
		},
		onSuccess: result => {
			// Invalidate and update contact queries - match the default useContact query key structure
			queryClient.invalidateQueries({
				queryKey: [CONTACT_QUERY_KEY, { allowArchived: false, contactId: result.id, expandHouseholdMembers: false }],
			});
			onSuccess?.(result);
		},
		onError,
	});
}
